import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Datetime from 'react-datetime';
import { format } from 'date-fns';
import moment from 'moment';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Container,
} from 'reactstrap';
import { ApplePayButton, Button, SuggestApplePayButton } from 'components/Button';
import Input from 'components/Form/Input';
import { checkIfApplePayIsAvailable } from 'utils/helper';
import { formatCurrency } from 'utils/formatCurrency';
import { floatUnmask } from 'utils/masks';
import LtvSummaryCard from './LtvSummaryCard';
import PaymentActionToggle, { LOWER_LTV_ACTIONS } from './PaymentActionToggle';
import LowerLtvPaymentForm from './LowerLtvPaymentForm';
import LowerLtvCollateralDeposit from './LowerLtvCollateralDeposit';
import ChooseYourGoal from './ChooseYourGoal';
import GoalAmountSummary from './GoalAmountSummary';
import {
  buildCollateralGoalOptionsFromDisplay,
  getCollateralRequiredDisplay,
  resolveFirstDefined,
  toLtvPercentage,
} from '../../utils/cryptoUtils';
import './style.scss';

const CRYPTO_GOAL_TYPES = {
  MINIMUM: 'minimum',
  SUGGESTED: 'suggested',
};

const PAYMENT_AMOUNT_GOAL = 'goal';

const getResolvedPaymentAmount = ({
  paymentAmountType,
  customPaymentAmount,
  totalDueAmount,
  pastDueAmount,
  dueFees,
  goalPaymentAmount,
}) => {
  switch (paymentAmountType) {
    case PAYMENT_AMOUNT_GOAL:
      return goalPaymentAmount == null
        ? totalDueAmount
        : formatCurrency(goalPaymentAmount, 2);
    case '1':
      return formatCurrency(
        Number(floatUnmask(pastDueAmount)) + Number(floatUnmask(dueFees)),
        2
      );
    case '2':
      return customPaymentAmount;
    default:
      return totalDueAmount;
  }
};

const getResolvedPaymentAmountValue = ({
  paymentAmountType,
  customPaymentAmount,
  totalDueAmount,
  pastDueAmount,
  dueFees,
  goalPaymentAmount,
  asNumber = false,
}) => {
  const raw = floatUnmask(getResolvedPaymentAmount({
    paymentAmountType,
    customPaymentAmount,
    totalDueAmount,
    pastDueAmount,
    dueFees,
    goalPaymentAmount,
  }));
  return asNumber ? Number(raw) : raw;
};

const LowerLtvModal = ({
  isOpen,
  toggle,
  lowerLtvData,
  paymentFormDefaults,
  onMakePayment,
  collateralRequired,
  isCollateralRequiredLoading,
  loanId,
}) => {
  const [activeAction, setActiveAction] = useState(LOWER_LTV_ACTIONS.COLLATERAL);
  const [selectedGoal, setSelectedGoal] = useState(CRYPTO_GOAL_TYPES.MINIMUM);
  const [payFromAccount, setPayFromAccount] = useState('');
  const [paymentAmountType, setPaymentAmountType] = useState('0');
  const [customPaymentAmount, setCustomPaymentAmount] = useState('');
  const [editablePaymentDate, setEditablePaymentDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isApplePayAvailable = checkIfApplePayIsAvailable();

  const {
    currentLtv,
    collateralHeld,
    contractualLtvDisplay,
    contractualLtvPct,
    marginCallThreshold,
    liquidationThreshold,
    addCollateralBtc,
    coinType,
    address,
  } = lowerLtvData;

  const {
    payFromAccounts = [],
    primaryPaymentMethod = '',
    paymentDate = '',
    totalDueAmount = '0.00',
    pastDueAmount = '0.00',
    dueFees = '0.00',
    nextPaymentDueDate = '',
    paymentAmountOptions = [],
  } = paymentFormDefaults;

  const collateralRequiredData = useMemo(
    () => getCollateralRequiredDisplay(collateralRequired),
    [collateralRequired]
  );

  const currentLtvPct = toLtvPercentage(currentLtv);
  const isLtvAboveContractualOnly = contractualLtvPct != null
    && currentLtvPct != null
    && currentLtvPct > contractualLtvPct
    && (marginCallThreshold == null || currentLtvPct < marginCallThreshold);
  const showGoalPicker = !isLtvAboveContractualOnly;
  // Below margin call: allow editing payment amount + date. At/above: account only.
  const allowAmountAndDateEdit = !(
    currentLtvPct != null
    && marginCallThreshold != null
    && currentLtvPct >= marginCallThreshold
  );
  const targetLtvTrackLabel = contractualLtvPct == null ? '-' : Math.round(contractualLtvPct);

  const goalOptions = useMemo(
    () => {
      if (!showGoalPicker) return [];

      const collateralOptions = buildCollateralGoalOptionsFromDisplay(collateralRequiredData, coinType, {
        ensureBothCards: true,
        targetLtvTrackLabel,
      });

      if (activeAction === LOWER_LTV_ACTIONS.PAYMENT) {
        return collateralOptions.map((option) => {
          if (option.key === 'minimum') {
            const payAmt = collateralRequiredData.minimumPaymentRequired;
            return {
              ...option,
              amount: payAmt != null ? String(payAmt) : '',
              displayAmount: payAmt != null ? `$${formatCurrency(payAmt, 2)}` : '-',
            };
          }
          const payAmt = resolveFirstDefined(
            collateralRequiredData.paymentRequired,
            collateralRequiredData.suggestedPaymentRequired
          );
          return {
            ...option,
            amount: payAmt != null ? String(payAmt) : '',
            displayAmount: payAmt != null ? `$${formatCurrency(payAmt, 2)}` : '-',
          };
        });
      }

      return collateralOptions;
    },
    [collateralRequiredData, coinType, showGoalPicker, targetLtvTrackLabel, activeAction]
  );

  const activeGoal = useMemo(
    () => goalOptions.find((option) => option.key === selectedGoal) || goalOptions[0],
    [goalOptions, selectedGoal]
  );

  useEffect(() => {
    if (!isOpen || !goalOptions.length) {
      return;
    }

    if (!goalOptions.some((option) => option.key === selectedGoal)) {
      setSelectedGoal(goalOptions[0].key);
    }
  }, [isOpen, goalOptions, selectedGoal]);

  const depositAmount = showGoalPicker
    ? resolveFirstDefined(
      activeGoal?.amount,
      selectedGoal === CRYPTO_GOAL_TYPES.MINIMUM
        ? collateralRequiredData.minimumQuantityRequired
        : collateralRequiredData.suggestedQuantityRequired,
      collateralRequiredData.quantityRequired,
      addCollateralBtc
    )
    : resolveFirstDefined(
      collateralRequiredData.quantityRequired,
      collateralRequiredData.suggestedQuantityRequired
    );
  const goalLinkedPaymentAmount = showGoalPicker
    ? resolveFirstDefined(
      activeGoal?.paymentAmount,
      selectedGoal === CRYPTO_GOAL_TYPES.MINIMUM
        ? collateralRequiredData.minimumPaymentRequired
        : resolveFirstDefined(
          collateralRequiredData.paymentRequired,
          collateralRequiredData.suggestedPaymentRequired
        ),
      collateralRequiredData.paymentRequired,
      collateralRequiredData.minimumPaymentRequired,
      collateralRequiredData.suggestedPaymentRequired
    )
    : resolveFirstDefined(
      collateralRequiredData.paymentRequired,
      collateralRequiredData.suggestedPaymentRequired
    );

  const goalSummaryLabel = useMemo(() => {
    if (activeAction === LOWER_LTV_ACTIONS.COLLATERAL) {
      return 'TRANSFER AMOUNT';
    }

    return `${(activeGoal?.label || 'Suggested').toUpperCase()} EXTRA PAYMENT`;
  }, [activeAction, activeGoal?.label]);

  const goalSummaryDescription = useMemo(() => {
    if (activeAction === LOWER_LTV_ACTIONS.PAYMENT) {
      if (showGoalPicker && selectedGoal === CRYPTO_GOAL_TYPES.MINIMUM) {
        return 'Minimum to stop your margin call';
      }

      return `Suggested: returns to target ${contractualLtvDisplay} LTV`;
    }

    return activeGoal?.description || `Returns to your contract LTV of ${contractualLtvDisplay}.`;
  }, [activeAction, showGoalPicker, selectedGoal, activeGoal?.description, contractualLtvDisplay]);

  const payFromAccountLabel = useMemo(() => {
    const account = payFromAccounts.find((item) => item.value === payFromAccount);
    return account?.title || '';
  }, [payFromAccounts, payFromAccount]);

  const paymentAmountLabel = useMemo(() => {
    if (paymentAmountType === PAYMENT_AMOUNT_GOAL && goalLinkedPaymentAmount != null) {
      const goalTag = (activeGoal?.label || 'suggested').toLowerCase();
      return `$${formatCurrency(goalLinkedPaymentAmount, 2)} (${goalTag})`;
    }
    const option = paymentAmountOptions.find((item) => item.value === paymentAmountType);
    if (paymentAmountType === '2' && customPaymentAmount) {
      return customPaymentAmount;
    }
    return option?.title || totalDueAmount;
  }, [
    paymentAmountOptions,
    paymentAmountType,
    customPaymentAmount,
    totalDueAmount,
    goalLinkedPaymentAmount,
    activeGoal?.label,
  ]);

  const isGoalLinkedPaymentAmount = paymentAmountType === PAYMENT_AMOUNT_GOAL
    && goalLinkedPaymentAmount != null;

  const effectivePaymentAmountOptions = useMemo(() => {
    if (activeAction !== LOWER_LTV_ACTIONS.PAYMENT || goalLinkedPaymentAmount == null) {
      return paymentAmountOptions;
    }

    const goalLabel = `${activeGoal?.label || 'Suggested'} - $${formatCurrency(goalLinkedPaymentAmount, 2)}`;

    return [
      { title: goalLabel, value: PAYMENT_AMOUNT_GOAL },
      ...paymentAmountOptions,
    ];
  }, [
    activeAction,
    goalLinkedPaymentAmount,
    activeGoal?.label,
    paymentAmountOptions,
  ]);

  const modalTitle = currentLtvPct != null
    && marginCallThreshold != null
    && currentLtvPct >= marginCallThreshold
    ? 'Resolve Liquidation'
    : 'Protect Your Collateral';

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const defaultAccount = primaryPaymentMethod
      || payFromAccounts[0]?.value
      || '';

    setActiveAction(LOWER_LTV_ACTIONS.COLLATERAL);
    setPayFromAccount(defaultAccount);
    setPaymentAmountType('0');
    setCustomPaymentAmount('');
    setEditablePaymentDate(paymentDate || '');
    setIsSubmitting(false);
  }, [
    isOpen,
    payFromAccounts,
    primaryPaymentMethod,
    paymentDate,
  ]);

  useEffect(() => {
    if (!isOpen || activeAction !== LOWER_LTV_ACTIONS.PAYMENT) {
      return;
    }

    if (goalLinkedPaymentAmount != null) {
      setPaymentAmountType(PAYMENT_AMOUNT_GOAL);
      setCustomPaymentAmount(formatCurrency(goalLinkedPaymentAmount, 2));
    }
  }, [
    isOpen,
    activeAction,
    goalLinkedPaymentAmount,
    selectedGoal,
  ]);

  const handlePayFromAccountChange = (event) => {
    setPayFromAccount(event.target.value);
  };

  const handlePaymentAmountTypeChange = (event) => {
    setPaymentAmountType(event.target.value);
  };

  const handleCustomPaymentAmountChange = (event) => {
    setCustomPaymentAmount(event.target.value);
  };

  const handlePaymentDateChange = (date) => {
    if (date && typeof date.toISOString === 'function') {
      setEditablePaymentDate(format(new Date(date.toISOString()), 'MM/dd/yyyy'));
    } else {
      setEditablePaymentDate(date || '');
    }
  };

  const validatePaymentDate = (current) => (
    current.isAfter(moment().subtract(1, 'day'))
    && current.isBefore(moment().add(45, 'days'))
  );

  const renderPaymentDateField = ({ isDisabled } = {}) => (
    <div className="has-value hasValue w-100 mb-2">
      <span>
        Payment Date
        <em>Required</em>
      </span>
      <Datetime
        name="paymentDate"
        label="Payment Date"
        value={editablePaymentDate}
        onChange={isDisabled ? undefined : handlePaymentDateChange}
        closeOnSelect
        dateFormat="MM/DD/YYYY"
        timeFormat={false}
        placeHolder="MM/DD/YYYY"
        isValidDate={validatePaymentDate}
        inputProps={{ disabled: isDisabled }}
        renderInput={(propsInput) => (
          <Input
            isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
            isRequired
            isDisabled={isDisabled}
            {...propsInput}
          />
        )}
      />
    </div>
  );

  const handleMakePayment = () => {
    if (activeAction !== LOWER_LTV_ACTIONS.PAYMENT) {
      return;
    }

    if (typeof onMakePayment !== 'function') {
      return;
    }

    setIsSubmitting(true);

    onMakePayment({
      payFromAccount,
      paymentAmount: getResolvedPaymentAmountValue({
        paymentAmountType,
        customPaymentAmount,
        totalDueAmount,
        pastDueAmount,
        dueFees,
        goalPaymentAmount: goalLinkedPaymentAmount,
      }),
      paymentAmountType,
      paymentDate: editablePaymentDate || paymentDate,
      dueFees,
    }, (success) => {
      setIsSubmitting(false);
      if (success) {
        toggle();
      }
    });
  };

  const isPaymentSubmitDisabled = !payFromAccount
    || !paymentAmountType
    || (paymentAmountType === '2' && !customPaymentAmount)
    || (paymentAmountType === PAYMENT_AMOUNT_GOAL && goalLinkedPaymentAmount == null)
    || getResolvedPaymentAmountValue({
      paymentAmountType,
      customPaymentAmount,
      totalDueAmount,
      pastDueAmount,
      dueFees,
      goalPaymentAmount: goalLinkedPaymentAmount,
      asNumber: true,
    }) === 0;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="md"
      wrapClassName="lower-ltv-modal"
      centered
    >
      <ModalHeader className="lower-ltv-modal__header no-border" toggle={toggle}>
        {modalTitle}
        <button type="button" className="close" onClick={toggle} aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </ModalHeader>
      <ModalBody className="lower-ltv-modal__body pt-0">
        <Container fluid className="px-0">
          <LtvSummaryCard
            collateralHeld={collateralHeld}
            currentLtv={currentLtv}
            contractualLtvPct={lowerLtvData.contractualLtvPct}
            marginCallThreshold={marginCallThreshold}
            liquidationThreshold={liquidationThreshold}
          />

          <PaymentActionToggle
            activeAction={activeAction}
            onChange={setActiveAction}
          />

          {showGoalPicker && (
            <ChooseYourGoal
              options={goalOptions}
              selectedGoal={selectedGoal}
              onSelect={setSelectedGoal}
              isLoading={isCollateralRequiredLoading}
            />
          )}

          {activeAction === LOWER_LTV_ACTIONS.PAYMENT && !showGoalPicker && (
            <GoalAmountSummary
              variant="payment"
              amount={goalLinkedPaymentAmount}
              coinType={coinType}
              contractualLtvDisplay={contractualLtvDisplay}
              description={goalSummaryDescription}
              summaryLabel={goalSummaryLabel}
            />
          )}

          {activeAction === LOWER_LTV_ACTIONS.COLLATERAL && !showGoalPicker && (
            <GoalAmountSummary
              variant="deposit"
              amount={depositAmount}
              coinType={coinType}
              contractualLtvDisplay={contractualLtvDisplay}
              description={goalSummaryDescription}
              summaryLabel={goalSummaryLabel}
            />
          )}

          {activeAction === LOWER_LTV_ACTIONS.PAYMENT ? (
            <LowerLtvPaymentForm
              payFromAccounts={payFromAccounts}
              payFromAccount={payFromAccount}
              paymentAmountOptions={effectivePaymentAmountOptions}
              paymentAmountType={paymentAmountType}
              customPaymentAmount={customPaymentAmount}
              paymentDate={editablePaymentDate || paymentDate}
              payFromAccountLabel={payFromAccountLabel}
              paymentAmountLabel={paymentAmountLabel}
              isGoalLinkedPaymentAmount={isGoalLinkedPaymentAmount}
              onPayFromAccountChange={handlePayFromAccountChange}
              onPaymentAmountTypeChange={handlePaymentAmountTypeChange}
              onCustomPaymentAmountChange={handleCustomPaymentAmountChange}
              allowAmountAndDateEdit={allowAmountAndDateEdit}
              renderPaymentDate={renderPaymentDateField}
              pastDueAmount={Number(floatUnmask(pastDueAmount)) > 0 ? pastDueAmount : ''}
              regularPaymentAmount={totalDueAmount}
              nextPaymentDueDate={nextPaymentDueDate}
            />
          ) : (
            <LowerLtvCollateralDeposit
              depositAmount={depositAmount}
              coinType={coinType}
              walletAddress={address}
              contractualLtvDisplay={contractualLtvDisplay}
              regularPaymentAmount={totalDueAmount}
              nextPaymentDueDate={nextPaymentDueDate}
              pastDueAmount={Number(floatUnmask(pastDueAmount)) > 0 ? pastDueAmount : ''}
              showAmountBox={false}
            />
          )}

          {activeAction === LOWER_LTV_ACTIONS.PAYMENT && (
            <>
              <p
                className="lower-ltv-modal__authorization"
                style={{ fontSize: '13px' }}
              >
                By clicking &quot;Make Extra Payment&quot;, I authorize LendingUSA to make a one-time
                debit and/or charge from my account as designated above.
              </p>

              <Button
                type="button"
                color="primary"
                className="lower-ltv-modal__submit-btn w-100"
                isLoading={isSubmitting}
                isDisabled={isPaymentSubmitDisabled}
                onClick={handleMakePayment}
              >
                Make Extra Payment
              </Button>
              {isApplePayAvailable ? (
                <ApplePayButton
                  className="w-100"
                  getAmountToPay={() => getResolvedPaymentAmountValue({
                    paymentAmountType,
                    customPaymentAmount,
                    totalDueAmount,
                    pastDueAmount,
                    dueFees,
                    goalPaymentAmount: goalLinkedPaymentAmount,
                    asNumber: true,
                  })}
                  processPayment={(applePayData, successCallback, failCallback) => {
                    if (typeof onMakePayment !== 'function') return;
                    setIsSubmitting(true);
                    onMakePayment({
                      payFromAccount,
                      paymentAmount: getResolvedPaymentAmountValue({
                        paymentAmountType,
                        customPaymentAmount,
                        totalDueAmount,
                        pastDueAmount,
                        dueFees,
                        goalPaymentAmount: goalLinkedPaymentAmount,
                      }),
                      paymentAmountType,
                      paymentDate: editablePaymentDate || paymentDate,
                      dueFees,
                      applePay: applePayData,
                    }, (success) => {
                      setIsSubmitting(false);
                      if (success) {
                        toggle();
                        successCallback && successCallback();
                      } else {
                        failCallback && failCallback();
                      }
                    });
                  }}
                  validatePayment={() => !isPaymentSubmitDisabled}
                />
              ) : <SuggestApplePayButton loanId={loanId} />}
            </>
          )}
        </Container>
      </ModalBody>
    </Modal>
  );
};

LowerLtvModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  lowerLtvData: PropTypes.shape({
    currentLtv: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contractualLtvDisplay: PropTypes.string,
    contractualLtvPct: PropTypes.number,
    collateralHeld: PropTypes.string,
    suggestedPaymentAmount: PropTypes.number,
    marginCallThreshold: PropTypes.number,
    liquidationThreshold: PropTypes.number,
    addCollateralBtc: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    payAmountUsd: PropTypes.number,
    coinType: PropTypes.string,
    address: PropTypes.string,
  }).isRequired,
  paymentFormDefaults: PropTypes.shape({
    payFromAccounts: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })),
    primaryPaymentMethod: PropTypes.string,
    paymentDate: PropTypes.string,
    totalDueAmount: PropTypes.string,
    pastDueAmount: PropTypes.string,
    dueFees: PropTypes.string,
    nextPaymentDueDate: PropTypes.string,
    paymentAmountOptions: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })),
  }),
  onMakePayment: PropTypes.func,
  collateralRequired: PropTypes.object,
  isCollateralRequiredLoading: PropTypes.bool,
  loanId: PropTypes.string,
};

LowerLtvModal.defaultProps = {
  paymentFormDefaults: {},
  onMakePayment: undefined,
  collateralRequired: null,
  isCollateralRequiredLoading: false,
  loanId: '',
};

export default LowerLtvModal;
